import { Bell } from "lucide-react";
import { useId, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";

import { formatCount } from "@lytra/frontend-common/components/v2/ui/nav";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@lytra/frontend-common/components/v2/ui/popover";
import { buttonVariants } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FuerDichList } from "@/components/notifications/FuerDichList";
import { useFuerDichCount } from "@/components/notifications/useFuerDich";
import { cn } from "@/lib/utils";
import { useProfile } from "@/providers/ProfileProvider";

export interface FuerDichBellButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> {
  /** Open entries; null until the first answer (no badge, no spinner). */
  count: number | null;
  isCollapsed: boolean;
  /** The popover is open: the rail's tooltip steps aside. */
  open?: boolean;
  /** React 19 passes the ref as a prop: the popover trigger needs the button itself. */
  ref?: React.Ref<HTMLButtonElement>;
}

/**
 * The bell as the sidebar draws it: v1 nav visuals so it sits flush with the neighbouring
 * items, expanded (icon, label, count on the right) or as the collapsed rail's icon with
 * the count on its corner. The badge is the one accent fill in the sidebar and is always the
 * primary brand token, never red and never the staff orange.
 */
export function FuerDichBellButton({
  count,
  isCollapsed,
  open = false,
  className,
  ref,
  ...props
}: FuerDichBellButtonProps) {
  const { t } = useTranslation();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  // Announce an increase only: never the first answer, never a decrease. The previous
  // count is state compared during render (React's "adjust state on prop change" pattern),
  // so no ref is written while rendering.
  const [seen, setSeen] = useState<{
    last: number | null;
    announced: number | null;
  }>({ last: count, announced: null });
  if (count !== null && count !== seen.last) {
    setSeen({
      last: count,
      announced: seen.last !== null && count > seen.last ? count : null,
    });
  }

  const shown = count ?? 0;
  const name =
    shown === 0
      ? t("notifications.bell.nameZero")
      : shown === 1
        ? t("notifications.bell.nameOne")
        : t("notifications.bell.nameOther", { count: shown });

  const badge =
    shown > 0 ? (
      <span
        aria-hidden="true"
        data-slot="count-badge"
        className={cn(
          "flex h-4 min-w-4 items-center justify-center rounded-sm bg-primary px-1 text-micro tabular-nums text-primary-foreground",
          isCollapsed ? "absolute -right-1 -top-1" : "ml-auto",
        )}
      >
        {formatCount(shown)}
      </span>
    ) : null;

  const status = (
    <span role="status" aria-live="polite" className="sr-only">
      {seen.announced !== null
        ? t("notifications.bell.announce", { count: seen.announced })
        : ""}
    </span>
  );

  // Outside the button: a live region inside a button is not announced reliably.
  const control = isCollapsed ? (
    <Tooltip
      delayDuration={0}
      open={tooltipOpen && !open}
      onOpenChange={setTooltipOpen}
    >
      <TooltipTrigger asChild>
        <button
          ref={ref}
          type="button"
          aria-label={name}
          {...props}
          className={cn(
            buttonVariants({ variant: "ghost", size: "icon" }),
            "relative h-9 w-9",
            className,
          )}
        >
          <Bell className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
          {badge}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">
        {shown > 0
          ? t("notifications.bell.tooltipCount", { count: shown })
          : t("notifications.title")}
      </TooltipContent>
    </Tooltip>
  ) : (
    <button
      ref={ref}
      type="button"
      aria-label={name}
      {...props}
      className={cn(
        buttonVariants({ variant: "ghost", size: "sm" }),
        "w-full justify-start data-[state=open]:bg-muted",
        className,
      )}
    >
      <Bell className="mr-2 h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
      {t("notifications.title")}
      {badge}
    </button>
  );

  return (
    <>
      {control}
      {status}
    </>
  );
}

// From `md` up the popover opens beside the sidebar; below it the sidebar is the 280px
// mobile sheet and the popover opens under the bell, bounded by the screen. There is no
// shared hook for this, so the one query is subscribed to here.
const WIDE_QUERY = "(min-width: 768px)";

function subscribeWide(onChange: () => void) {
  const query = window.matchMedia(WIDE_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

function useIsWide(): boolean {
  return useSyncExternalStore(
    subscribeWide,
    () => window.matchMedia(WIDE_QUERY).matches,
    () => true,
  );
}

/**
 * The "Für dich" bell at the top of the sidebar (#807) and the popover list it
 * opens. Agents and admins only, the same gate the server enforces on every field, so nobody
 * else fires the query or the subscription. The hooks run unconditionally; `enabled` skips
 * the query.
 */
export function FuerDichBell({ isCollapsed }: { isCollapsed: boolean }) {
  const { userRoles } = useProfile();
  const isAgentOrAdmin = userRoles.isAgent || userRoles.isAdmin;
  const { count, changeTick, refetch } = useFuerDichCount(isAgentOrAdmin);
  const [open, setOpen] = useState(false);
  const wide = useIsWide();
  const titleId = useId();

  if (!isAgentOrAdmin) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FuerDichBellButton
          count={count}
          isCollapsed={isCollapsed}
          open={open}
        />
      </PopoverTrigger>
      {/* 480px: the subject and the quote need the room; never wider than the viewport. */}
      <PopoverContent
        width="auto"
        padding="none"
        align="start"
        side={wide ? "right" : "bottom"}
        aria-labelledby={titleId}
        className="w-[min(480px,calc(100vw-16px))]"
      >
        <FuerDichList
          open={open}
          openCount={count}
          onClose={() => setOpen(false)}
          onCountStale={refetch}
          changeSignal={changeTick}
          titleId={titleId}
        />
      </PopoverContent>
    </Popover>
  );
}
